import * as _ from "lodash";
import { MetricDefinitionBO } from "./MetricDefinition.BO";
import { PASearch } from "../PA.Core/PASearch";
import { ErrorMessage_restate, WarningMessage_restate } from "../PA.Core/Basic/MessageUtils";
import { WB_MetricDefinitionRow, WB_PlanVolumeMetricsRow, WB_TPM_EventMetricsRow } from "../PA.Core/PAdbSchemaRows";
import { parse } from "mathjs";
import { ICubeSourceSearchMetricIdOptions } from "../PA.HyperRectangle.Services/PA.HyperRectangle.Services.IF";
import { CubeSourceMetaDataService } from "../PA.HyperRectangle.Services/CubeSourceMetaDataService";
import { WB_Plan_Volume_Metrics, WB_TPM_Event_Metrics } from "../PA.Core/PAdbSchema.Other";

enum METRIC_TYPES {
    foundation = "foundation",
    compound = "compound",
    dataEntry = "dataEntry",
    cubeSourceRow = "csRow",
    cubeSourcePerspective = "csPerspective",
}

const aWB_PlanVolumeMetricRow = new WB_PlanVolumeMetricsRow({}, {});
const aWB_EventVolumeMetricRow = new WB_TPM_EventMetricsRow({}, {});

export class MetricDefinitionBOValidations {
    private static invalidFdnMtricId: string = "";

    static async BeforeSave(aBO: MetricDefinitionBO): Promise<void> {
        if (!(aBO.WB_MetricDefinition.isActive || aBO.WB_MetricDefinition.isActiveNew)) {
            return;
        }

        const self = this;
        const mdr = aBO.WB_MetricDefinition;
        const md = aBO.WB_MetricDefinition.cif;

        switch (md.METRIC_TYPE) {
            case METRIC_TYPES.foundation:
                md.WB_CUBESOURCE_ID = 0;
                break;

            case METRIC_TYPES.compound:
                md.WB_CUBESOURCE_ID = 0;
                break;

            case METRIC_TYPES.dataEntry:
                md.WB_CUBESOURCE_ID = 0;
                break;

            case METRIC_TYPES.cubeSourceRow:
                md.EDITABLE_IND = "0";

                break;

            case METRIC_TYPES.cubeSourcePerspective:
                md.UOM_CONVERTER_NUM = "";
                md.UOM_CONVERTER_DENOM = "";
                if (!mdr.bEditable) {
                    md.WRITEBACK_METRIC_ID = "";
                    md.WRITEBACK_FORMULA = "";
                }
                break;
        }
    }

    static async ValidateFields(aBO: MetricDefinitionBO): Promise<void> {
        await MetricDefinitionBOValidations.BeforeSave(aBO);

        //record status
        if (!(aBO.WB_MetricDefinition.isActive || aBO.WB_MetricDefinition.isActiveNew)) {
            return;
        }

        const self = this;
        const md = aBO.WB_MetricDefinition.cif;

        let rgx = /^[A-Z|_]+$/;

        //validate Metric ID
        ErrorMessage_restate(aBO.ValidateMessageProvider, _.isEmpty(md.METRIC_ID), "EmptyId", {
            Title: "Metric Definition ID cannot be empty",
            Desc: "",
        });

        //validate Metric Name
        WarningMessage_restate(aBO.ValidateMessageProvider, _.isEmpty(md.METRIC_NAME), "EmptyName", {
            Title: "Metric Definition Name cannot be empty",
            Desc: "",
        });

        if (aBO.WB_MetricDefinition.cif.EVTS_TYPE_FILTER_IND == "1") {
            ErrorMessage_restate(
                aBO.ValidateMessageProvider,
                _.isEmpty(_.filter(aBO.WB_MetricDefinition.WB_MetricDefinitionEventSpendRows, (row) => row.isActive)),
                "EmpSpendTypes",
                {
                    Title: "Metric Definition Spend Types cannot be empty",
                    Desc: "",
                },
            );
        }
        let wbCubeSource = _.toNumber(md.WB_CUBESOURCE_ID);
        let bInvalidCubeSource = !_.isNumber(wbCubeSource) || wbCubeSource <= 0;

        //perform validation on metric types
        let givenMtrIds: string[] = undefined;
        switch (md.METRIC_TYPE) {
            case METRIC_TYPES.foundation:
                //validate fdnMtr column name
                ErrorMessage_restate(
                    aBO.ValidateMessageProvider,
                    !rgx.test(md.SUBJECTAREA_COLUMNNAME),
                    "InvalidClmnName",
                    { Title: "Only uppercase letters and/or _(underscore) are allowed", Desc: "" },
                );
                break;

            case METRIC_TYPES.compound:
                givenMtrIds = this.getCompoundMetricIds(md.FORMULA);

                //validate input for balanced parenthesis
                ErrorMessage_restate(
                    aBO.ValidateMessageProvider,
                    !(
                        MetricDefinitionBOValidations.isBalancedParenthesis(md.FORMULA) &&
                        MetricDefinitionBOValidations.isBalancedCurlyParenthesis(md.FORMULA)
                    ),
                    "unbalancedParenthesis",
                    { Title: "The parentheses in the formula are unbalanced", Desc: "" },
                );

                ErrorMessage_restate(
                    aBO.ValidateMessageProvider,
                    givenMtrIds.includes(md.METRIC_ID),
                    "selfReferencing",
                    { Title: "A compound metric cannot use itself as a component metric", Desc: ""}
                )

                //validate input for valid compoundfdnMtrcId

                await MetricDefinitionBOValidations.isInvalidCompoundfdnMtrcId(givenMtrIds, md.FORMULA)
                    .then((bInputValid) => {
                        ErrorMessage_restate(aBO.ValidateMessageProvider, bInputValid, "InvalidCompoundfdnMtrcId", {
                            Title: self.invalidFdnMtricId + " isn't a valid Foundation/Compound Metric Id",
                            Desc: "",
                        });
                    })
                    .then(() => {
                        let bCompoundFormulaValid = MetricDefinitionBOValidations.isInvalidFormula(
                            givenMtrIds,
                            md.FORMULA,
                        );
                        ErrorMessage_restate(aBO.ValidateMessageProvider, bCompoundFormulaValid, "invalidFormula", {
                            Title: "The formula can't be calculated",
                            Desc: "",
                        });
                    });

                break;

            case METRIC_TYPES.dataEntry:
                //validate Metric Usage
                ErrorMessage_restate(aBO.ValidateMessageProvider, _.isEmpty(md.METRIC_USAGE), "EmptyId", {
                    Title: "Metric Usage cannot be empty",
                    Desc: "",
                });

                //validate input for Total Aggregation Terms
                let bMissingParenthesis = !(
                    MetricDefinitionBOValidations.isBalancedParenthesis(md.FORMULA) &&
                    MetricDefinitionBOValidations.isBalancedCurlyParenthesis(md.FORMULA)
                );
                ErrorMessage_restate(
                    aBO.ValidateMessageProvider,
                    bMissingParenthesis,
                    "unbalancedParenthesisinformula",
                    {
                        Title: "The parentheses in the formula are unbalanced",
                        Desc: "",
                    },
                );

                bMissingParenthesis = !(
                    MetricDefinitionBOValidations.isBalancedParenthesis(md.TOTAL_AGGREGATION_TERMS) &&
                    MetricDefinitionBOValidations.isBalancedCurlyParenthesis(md.TOTAL_AGGREGATION_TERMS)
                );
                ErrorMessage_restate(
                    aBO.ValidateMessageProvider,
                    bMissingParenthesis,
                    "unbalancedParenthesisintotalterms",
                    {
                        Title: "The parentheses in the total aggregation terms are unbalanced",
                        Desc: "",
                    },
                );

                let objectErr = MetricDefinitionBOValidations.validateDataEntryObjectType(aBO.WB_MetricDefinition);
                ErrorMessage_restate(aBO.ValidateMessageProvider, objectErr.length > 0, "unparseObjects", {
                    Title: objectErr + " properties don't match with expected, please check their patterns",
                    Desc: "",
                });

                givenMtrIds = this.getMetricIds(md.FORMULA);
                let metricNames = MetricDefinitionBOValidations.isInvalidDataEntryMtrcId(givenMtrIds);
                ErrorMessage_restate(aBO.ValidateMessageProvider, metricNames.length > 0, "InvalidDataEntryMtrcId", {
                    Title:
                        metricNames.substring(0, metricNames.length - 1) +
                        " not valid Metric Data Column(s) in formula",
                    Desc: "",
                });

                metricNames = MetricDefinitionBOValidations.isInvalidDataEntryMtrcId(md.RECORD_PROPERTIES.split(","));
                ErrorMessage_restate(aBO.ValidateMessageProvider, metricNames.length > 0, "InvalidDataEntryMtrcId", {
                    Title:
                        metricNames.substring(0, metricNames.length - 1) +
                        " not valid Metric Data Column(s) in record properties",
                    Desc: "",
                });

                let bColumnValid = MetricDefinitionBOValidations.validateTerm(aBO.WB_MetricDefinition);
                ErrorMessage_restate(aBO.ValidateMessageProvider, !bColumnValid, "Invalidtermmetric", {
                    Title: "Aggregation Terms Not in record properties",
                    Desc: "",
                });

                bColumnValid = MetricDefinitionBOValidations.validateTermMetric(aBO.WB_MetricDefinition);
                ErrorMessage_restate(aBO.ValidateMessageProvider, !bColumnValid, "Invalidtermcase", {
                    Title: "Only uppercase letters and/or _(underscore) are allowed for metrics in Aggregation Terms",
                    Desc: "",
                });

                let bDataEntryFormulaValid = MetricDefinitionBOValidations.isInvalidFormula(givenMtrIds, md.FORMULA);
                ErrorMessage_restate(aBO.ValidateMessageProvider, bDataEntryFormulaValid, "invalidFormula", {
                    Title: "The formula can't be calculated",
                    Desc: "",
                });

                break;

            case METRIC_TYPES.cubeSourceRow:
                ErrorMessage_restate(
                    aBO.ValidateMessageProvider,
                    md.DECIMAL_COUNT < 0 || md.DECIMAL_COUNT > 4,
                    "DecimalCountInvalid",
                    { Title: "Decimals should be between 0 and 4", Desc: "" },
                );

                ErrorMessage_restate(aBO.ValidateMessageProvider, bInvalidCubeSource, "InvalidCubeSource", {
                    Title: "CubeSource not selected",
                    Desc: "",
                });

                if (!bInvalidCubeSource) await MetricDefinitionBOValidations.validateAndSyncCubeSourceFormula(aBO);
                break;

            case METRIC_TYPES.cubeSourcePerspective:
                ErrorMessage_restate(
                    aBO.ValidateMessageProvider,
                    md.DECIMAL_COUNT < 0 || md.DECIMAL_COUNT > 4,
                    "DecimalCountInvalid",
                    { Title: "Decimals should be between 0 and 4", Desc: "" },
                );

                ErrorMessage_restate(aBO.ValidateMessageProvider, bInvalidCubeSource, "InvalidCubeSource", {
                    Title: "CubeSource not selected",
                    Desc: "",
                });

                if (!bInvalidCubeSource) await MetricDefinitionBOValidations.validateAndSyncCubeSourceFormula(aBO);

                break;
        }
    }

    static async validateAndSyncCubeSourceFormula(aBO: MetricDefinitionBO): Promise<void> {
        const self = this;
        const mdr = aBO.WB_MetricDefinition;
        const md = aBO.WB_MetricDefinition.cif;

        let wbCubeSource = _.toNumber(md.WB_CUBESOURCE_ID);
        let bValidFormula = true;
        switch (md.METRIC_TYPE) {
            case METRIC_TYPES.cubeSourceRow:
                bValidFormula = await validateFormula(md.FORMULA, { bColumnMetricsOnly: true }, false);
                break;

            case METRIC_TYPES.cubeSourcePerspective:
                bValidFormula = await validateFormula(md.FORMULA, { bColumnMetricHasAggregator: true }, false);
                if (mdr.bEditable)
                    bValidFormula ||= await validateFormula(md.WRITEBACK_FORMULA, { bColumnMetricsOnly: true }, true);
                break;
        }

        async function validateFormula(
            sFormulaString: string,
            metricIdOpts: ICubeSourceSearchMetricIdOptions,
            bIsWriteBack?: boolean,
        ): Promise<boolean> {
            let bIsValid = true;
            let dctSymbols: { [id: string]: string } = {};
            let parsedFormula = parse(md.FORMULA);
            parsedFormula.traverse((node: any, path: string, parent: any) => {
                if (node.type == "SymbolNode") dctSymbols[node.name] = node.name;
            });

            let mds = new CubeSourceMetaDataService();
            let arrCSMetrics = await mds.getCubeSourceMetricsIds(wbCubeSource, metricIdOpts);
            let dctCSMetrics = _.keyBy(arrCSMetrics, (a) => a.MetricId);

            for (let aTermId of _.values(dctSymbols)) {
                if (bIsWriteBack && aTermId == "Value") continue;

                if (aTermId in dctCSMetrics) continue;
                bIsValid = false;

                ErrorMessage_restate(
                    aBO.ValidateMessageProvider,
                    true,
                    `InvalidTerm_${aTermId}`,
                    `Invalid Terms ${aTermId} in formula ${sFormulaString}`,
                );
            }

            return bIsValid;
        }
    }

    static validateInput(input: any) {
        var inputRegex = /^[a-zA-Z][:][(][\S \s][)]=>[(][\S \s]+?[)]{1}$/;
        var inputArr = input.split("#");
        var validInputArr;

        if (inputArr && inputArr.length) {
            if (input.indexOf("#") !== -1) {
                validInputArr = inputArr.map((val: any) => {
                    return inputRegex.test(val.trim());
                });
            } else {
                validInputArr = inputArr.map((val: any) => {
                    if (input.indexOf(":") !== -1) {
                        return inputRegex.test(val.trim());
                    }
                    return false;
                });
            }
        }

        var newValidInput = validInputArr.every((ele: any) => {
            return ele;
        });
        console.log(newValidInput, "newValidInput");
        return newValidInput;
    }

    static isInvalidCompoundfdnMtrcId(givenMtrIds: string[], input: string): Promise<boolean> {
        return new Promise<boolean>((resolve, reject) => {
            let whereFieldSpecJSON: any = {
                WB_MTRDFN_ID: { type: "number" },
                WB_SUBJECTAREAD_ID: { type: "number" },
                MINVALUE: { type: "number" },
                MAXVALUE: { type: "number" },
            };

            let whereConditionJSON: any = {};

            //get a list of valid foundation and compound Mtrics
            let lstOfValidMtricIds: string[] = [];
            PASearch.find("vMTR_COMPOUNDTERM", whereConditionJSON, whereFieldSpecJSON, ["METRIC_ID ASC"], 0, [])
                .then((mtricIds) => {
                    _.forEach(mtricIds, (aMtrId: any) => {
                        lstOfValidMtricIds.push(aMtrId.METRIC_ID.toUpperCase());
                    });
                })
                .then(() => {
                    _.forEach(givenMtrIds, (aID) => {
                        if (!_.includes(lstOfValidMtricIds, aID.toUpperCase())) {
                            this.invalidFdnMtricId = aID;
                            return resolve(true);
                        }
                    });
                    //check if given metricIds are included in lstOfValidMtrIds
                    resolve(false);
                });
        });
    }

    static isInvalidDataEntryMtrcId(givenMtrIds: string[]): string {
        let metrics = "";
        _.forEach(givenMtrIds, (aID) => {
            let propertyName = aID.toUpperCase();
            switch (true) {
                case propertyName in aWB_PlanVolumeMetricRow:
                case propertyName in WB_Plan_Volume_Metrics.properties:
                case propertyName in aWB_EventVolumeMetricRow:
                case propertyName in WB_TPM_Event_Metrics.properties:
                    break;
                default:
                    metrics += aID + ",";
            }
        });
        return metrics;
    }

    static isBalancedParenthesis(input: string): boolean {
        let isBalanced = (str: string) => {
            return !str.split("").reduce((uptoPrevChar, thisChar) => {
                if (thisChar === "(") {
                    return ++uptoPrevChar;
                } else if (thisChar === ")") {
                    return --uptoPrevChar;
                }
                return uptoPrevChar;
            }, 0);
        };

        return isBalanced(input);
    }

    static isBalancedCurlyParenthesis(input: string): boolean {
        let isBalanced = (str: string) => {
            return !str.split("").reduce((uptoPrevChar, thisChar) => {
                if (thisChar === "{") {
                    return ++uptoPrevChar;
                } else if (thisChar === "}") {
                    return --uptoPrevChar;
                }
                return uptoPrevChar;
            }, 0);
        };

        return isBalanced(input);
    }

    static isInvalidFormula(factors: string[], input: string): boolean {
        let isValid = (factors: string[], input: string) => {
            var returnClause = "return " + input.replace(/\$\{/g, "").replace(/\}/g, "");
            var i = 0;
            _.forEach(factors, (aMtrId: any) => {
                returnClause = returnClause.replace(aMtrId, 'factors.split(",")[' + i + "]");
                i++;
            });

            var func = new Function("factors", returnClause);
            //    var arr0: number[] = new Array(factors.length).fill(0)
            var arr1: number[] = new Array(factors.length).fill(1);
            //     if (!isNaN(func(arr0.join(","))) && !isNaN(func(arr1.join(","))))
            if (!isNaN(func(arr1.join(",")))) return false;
            else return true;
        };

        return isValid(factors, input);
    }

    static getMetricIds(input: string): string[] {
        let regx = /\${[0-9a-zA-Z_]*}/g;
        let givenMtrIds: string[] = [];

        _.forEach(input.match(regx), (aId) => {
            givenMtrIds.push(aId.replace(/\$\{/g, "").replace(/\}/g, "").trim());
        });

        return givenMtrIds;
    }

    static getCompoundMetricIds(input: string): string[] {
        let regx = /[a-zA-Z_]+[0-9a-zA-Z_]*/g;
        let givenMtrIds: string[] = [];

        _.forEach(input.match(regx), (aId) => {
            givenMtrIds.push(aId.trim());
        });

        return givenMtrIds;
    }

    static validateTerm(input: WB_MetricDefinitionRow): boolean {
        let regx = /\.[0-9a-zA-Z_]*/g;
        let isValid = true;
        _.forEach(input.cif.TOTAL_AGGREGATION_TERMS.match(regx), (aId) => {
            if (!input.cif.RECORD_PROPERTIES.split(",").includes(aId.substring(1, aId.length).trim())) {
                return false;
            }
        });
        return isValid;
    }

    static validateTermMetric(input: WB_MetricDefinitionRow): boolean {
        let regx = /\.[0-9a-zA-Z_]*/g;
        let rgx = /^[A-Z|_]+$/;

        _.forEach(input.cif.TOTAL_AGGREGATION_TERMS.match(regx), (aId) => {
            if (!rgx.test(aId.substring(1, aId.length).trim())) {
                return false;
            }
        });
        return true;
    }

    static validateDataEntryObjectType(input: WB_MetricDefinitionRow): string {
        let errMessage = "";
        let format: {
            bShowThousandsSep?: boolean;
            iDecimalCount?: number;
            bShowSign?: boolean;
            bPercentage?: boolean;
            bShowCurrency?: boolean;
            sCurrencyField?: string;
            sDateFormat?: string;
        } = undefined;
        let renderOptions: { ui5?: { hAlign?: any; resourceId?: string } };

        try {
            format = JSON.parse(input.cif.METRIC_FORMAT);
        } catch (e) {
            errMessage = "Format ";
        }

        try {
            renderOptions = JSON.parse(input.cif.OPTIONS);
        } catch {
            if (errMessage.length) {
                errMessage += ",";
            }
            errMessage += "Render Option";
        }

        return errMessage;
    }
}
